import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import ProcessingModal from './components/ProcessingModal';
import ServiceChargeModal from './components/ServiceChargeModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ReadAloudButton from './components/ReadAloudButton';
import AudioPlayer from './components/AudioPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

function CCATPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author_name: '',
    publisher: '',
    genre: '',
    sub_genre: ''
  });
  
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  
  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating competitive analysis', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  const [success, setSuccess] = useState('');
  
  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  // Receipt modal state
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState({});
  const [lastCharge, setLastCharge] = useState(null);
  
  // TTS state
  const [audioUrl, setAudioUrl] = useState(null);
  const [currentPlayingSection, setCurrentPlayingSection] = useState(null);

  // Load report if report ID is in URL (from Dashboard page)
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadReport(reportId);
    }
  }, [searchParams]);

  const loadReport = async (reportId) => {
    try {
      setIsProcessing(true);
      const response = await axios.get(`${API}/api/ccat/reports/${reportId}`, {
        withCredentials: true
      });
      setResult(response.data);
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Failed to load report. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Function to clean text of markdown formatting (hashtags and asterisks)
  const cleanText = (text) => {
    if (!text) return '';
    return text
      .replace(/#{1,6}\s/g, '') // Remove hashtags
      .replace(/\*\*/g, '') // Remove bold asterisks
      .replace(/\*/g, '') // Remove italic asterisks
      .replace(/^\s*[-*]\s/gm, '• '); // Convert markdown bullets to bullets
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    setFileError('');
    
    if (selectedFile) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const fileExtension = selectedFile.name.toLowerCase().split('.').pop();
      
      
      if (!validTypes.includes(selectedFile.type) && !['pdf', 'docx'].includes(fileExtension)) {
        setFileError('Please upload a PDF or DOCX file');
        setFile(null);
        return;
      }
      
      // Validate file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError('File size must be under 10MB');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation
    if (!file) {
      setError('Please upload a manuscript file');
      return;
    }
    
    if (!formData.title.trim() || !formData.author_name.trim() || !formData.genre) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Show charge confirmation modal for non-subscribers (but don't charge yet)
    if (user && user.subscription_status === 'None' && user.role !== 'admin' && user.role !== 'beta_tester') {
      setChargeInfo({
        amount: 7.00,
        service: 'ccat',
        name: 'Competitor & Comparative Analysis Tool (CCAT)',
        serviceName: 'Competitor & Comparative Analysis Tool (CCAT)',
        bookTitle: formData.title || 'CCAT Analysis'
      });
      setShowChargeModal(true);
      return; // Wait for user confirmation
    }
    
    // If subscriber or admin, proceed directly with analysis
    await submitForAnalysis();
  };
  
  const handleConfirmCharge = async () => {
    // User confirmed they want to proceed - close modal and submit
    setShowChargeModal(false);
    
    // Backend will handle the charge during analysis
    await submitForAnalysis();
  };
  
  const submitForAnalysis = async () => {
    setIsProcessing(true);
    
    try {
      // Step 1: Upload and analyze
      setCurrentStep('Uploading and analyzing manuscript...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: false, current: true },
        { text: 'Generating competitive analysis', completed: false, current: false },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      const submitFormData = new FormData();
      submitFormData.append('file', file);
      submitFormData.append('title', formData.title);
      submitFormData.append('subtitle', formData.subtitle);
      submitFormData.append('author_name', formData.author_name);
      submitFormData.append('publisher', formData.publisher);
      submitFormData.append('genre', formData.genre);
      submitFormData.append('sub_genre', formData.sub_genre);
      
      setProcessingProgress(25);
      
      // Step 2: Generate analysis
      setCurrentStep('Generating competitive analysis and market insights...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating competitive analysis', completed: false, current: true },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(
        `${API}/api/ccat/upload-and-analyze`,
        submitFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );
      
      // Step 3: Formatting
      setCurrentStep('Finalizing your competitive analysis report...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating competitive analysis', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      // Store charge info before reload
      const wasCharged = response.data.charged || false;
      const transactionId = response.data.transaction_id;
      const reportId = response.data.id;
      const chargeAmount = response.data.charge_amount || 7.00;
      
      
      // CRITICAL: Reload complete report from database to ensure all data is displayed
      if (reportId) {
        await loadAndDisplayReport(reportId);
      } else {
        // Fallback to using response data if no report_id
        setResult(response.data);
      }
      
      setSuccess('Competitive analysis completed successfully!');
      
      // Complete progress
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating competitive analysis', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Show receipt modal if charged
      if (wasCharged && transactionId) {
        setLastTransactionId(transactionId);
        setLastCharge({
          amount: chargeAmount,
          serviceName: "Competitor & Comparative Analysis Tool (CCAT)",
          bookTitle: formData.title || "CCAT Analysis"
        });
        
        // Show receipt modal after brief delay
        setTimeout(() => {
          setIsProcessing(false);
          setShowEmailReceiptModal(true);
        }, 500);
      } else {
        setIsProcessing(false);
      }
      
      // Don't clear formData - keep it for display
      setFile(null);
      
    } catch (err) {
      console.error('CCAT analysis error:', err);
      setIsProcessing(false);
      
      if (err.response?.status === 402 && err.response?.data?.requires_payment_setup) {
        setPaymentInfo(err.response.data);
        setShowPaymentModal(true);
      } else {
        setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
      }
    }
  };
  
  const loadAndDisplayReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/api/ccat/reports/${reportId}`, { 
        withCredentials: true 
      });
      
      setResult(response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error loading CCAT report:', error);
      throw error;
    }
  };

  const handlePrint = () => {
    if (!result) return;
    
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      // Format timestamp for header
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const generatedDate = now.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Digital Ink AI - CCAT Report</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; line-height: 1.5; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .ccat-header { text-align: center; margin-bottom: 20px; }
              .ccat-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .ccat-header h1 { font-size: 16pt; margin: 8px 0; font-family: 'Times New Roman', Times, serif; color: #0d9488; font-style: italic; }
              .ccat-header h2 { font-size: 18pt; margin: 5px 0; font-family: 'Times New Roman', Times, serif; font-weight: bold; }
              .ccat-header p { font-size: 11pt; margin: 3px 0; color: #666; }
              .generated-date { text-align: center; font-size: 10pt; color: #666; margin-top: 15px; }
              .manuscript-info { margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #ddd; }
              .manuscript-info h2 { font-size: 16pt; font-weight: bold; margin: 0 0 3px 0; font-family: 'Times New Roman', Times, serif; }
              .manuscript-info p { font-size: 12pt; margin: 3px 0; font-family: 'Times New Roman', Times, serif; }
              .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 15px 0; }
              .stat-card { border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 6px; }
              .stat-value { font-size: 16pt; font-weight: bold; margin-bottom: 3px; }
              .stat-label { font-size: 10pt; color: #666; }
              .section { margin-bottom: 15px; padding: 12px; border-left: 4px solid #0d9488; background-color: #f0fdfa; border-radius: 6px; page-break-inside: avoid; }
              .section h3 { font-size: 14pt; font-weight: bold; margin: 0 0 8px 0; font-family: 'Times New Roman', Times, serif; color: #134e4a; }
              .section p { margin: 0; font-size: 12pt; line-height: 1.6; font-family: 'Times New Roman', Times, serif; white-space: pre-wrap; }
              .footer { margin-top: 20px; text-align: center; font-size: 10pt; color: #666; border-top: 1px solid #ddd; padding-top: 12px; }
              @media print { body { margin: 10px; } .no-print { display: none; } }
            </style>
          </head>
          <body>
            <!-- Standard Header -->
            <div class="print-header">
              <div>${timestamp}</div>
              <div>Digital Ink AI - CCAT Report</div>
            </div>
            
            <div class="ccat-header">
              <img src="/Digital_Ink_AI_Logo.png" alt="Digital Ink AI" onload="window.print();" />
              <h1>Competitor & Comparative Analysis Tool</h1>
              <h2>${result.title || 'Manuscript Analysis'}</h2>
              <p>by ${result.author_name || 'Unknown Author'}</p>
            </div>
            
            <div class="generated-date">Generated ${generatedDate}</div>
            
            <!-- Manuscript Info -->
            <div class="manuscript-info">
              <h2>${result.title || 'Manuscript Analysis'}</h2>
              ${result.subtitle ? `<p style="font-style: italic; color: #475569;">${result.subtitle}</p>` : ''}
              <p style="font-weight: 600;">by ${result.author_name || 'Unknown Author'}</p>
              ${result.publisher ? `<p style="font-size: 11pt; color: #64748b; margin-top: 5px;">Publisher: ${result.publisher}</p>` : ''}
            </div>
            
            <!-- Statistics Grid -->
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-value" style="color: #0d9488;">${result.word_count?.toLocaleString() || 'N/A'}</div>
                <div class="stat-label">Word Count</div>
              </div>
              <div class="stat-card">
                <div class="stat-value" style="color: #16a34a;">${result.page_count || 'N/A'}</div>
                <div class="stat-label">Est. Pages</div>
              </div>
              ${result.genre ? `
              <div class="stat-card">
                <div class="stat-value" style="color: #9333ea; font-size: 13pt;">${result.genre.replace('_', ' ').toUpperCase()}</div>
                <div class="stat-label">Genre</div>
              </div>` : ''}
              ${result.sub_genre ? `
              <div class="stat-card">
                <div class="stat-value" style="color: #4f46e5; font-size: 13pt;">${result.sub_genre}</div>
                <div class="stat-label">Sub-Genre</div>
              </div>` : ''}
            </div>
            
            <!-- 6 CCAT Sections -->
            <div class="report-content">
              
              ${result.book_information_overview ? `
              <div class="section">
                <h3>📖 Book Information Overview</h3>
                <p>${result.book_information_overview}</p>
              </div>` : ''}
              
              ${result.competitor_identification ? `
              <div class="section">
                <h3>📚 Competitor Identification</h3>
                <p>${result.competitor_identification}</p>
              </div>` : ''}
              
              ${result.market_gaps_analysis ? `
              <div class="section">
                <h3>🔍 Market Gaps & Opportunities</h3>
                <p>${result.market_gaps_analysis}</p>
              </div>` : ''}
              
              ${result.differentiation_strategies ? `
              <div class="section">
                <h3>💡 Differentiation Strategies</h3>
                <p>${result.differentiation_strategies}</p>
              </div>` : ''}
              
              ${result.recommendations ? `
              <div class="section">
                <h3>📋 Recommendations & Action Plan</h3>
                <p>${result.recommendations}</p>
              </div>` : ''}
              
              ${result.final_summary ? `
              <div class="section" style="background-color: #dbeafe; border-left-color: #2563eb;">
                <h3 style="color: #1e40af;">📄 Executive Summary</h3>
                <p>${result.final_summary}</p>
              </div>` : ''}
              
            </div>
            
            <!-- Footer -->
            <div class="footer">
              <p>Generated by Digital Ink AI - Competitor & Comparative Analysis Tool</p>
              <p>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    }
  };

  const exportToPDF = () => {
    if (!result) return;
    
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Helper function to add text with word wrap
      const addText = (text, fontSize = 11, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('times', isBold ? 'bold' : 'normal');
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          if (yPos + 10 > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
          }
          pdf.text(line, margin, yPos);
          yPos += fontSize * 0.4;
        });
        yPos += 3;
      };
      
      // Header
      pdf.setFontSize(18);
      pdf.setFont('times', 'bold');
      pdf.text('COMPETITOR AND COMPARATIVE ANALYSIS', margin, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 8;
      
      // Parse and render the analysis report
      const reportText = result.analysis_report;
      const sections = reportText.split('\n\n');
      
      sections.forEach(section => {
        if (!section.trim()) return;
        
        if (section.startsWith('**') && section.includes('**')) {
          // Bold section headers
          const headerMatch = section.match(/\*\*(.*?)\*\*/);
          if (headerMatch) {
            addText(headerMatch[1], 12, true);
            const remainingText = section.replace(/\*\*.*?\*\*/, '').trim();
            if (remainingText) {
              addText(remainingText, 11, false);
            }
          } else {
            addText(section, 11, false);
          }
        } else {
          addText(section, 11, false);
        }
      });
      
      // Save PDF
      const fileName = `CCAT_${formData.title.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try printing instead.');
    }
  };

  const handleReadAloud = async (section, sectionName) => {
    try {
      setCurrentPlayingSection(sectionName);
      
      const response = await axios.post(
        `${API}/api/tts/read-aloud`,
        { text: section },
        {
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(audioUrl);
      
    } catch (err) {
      console.error('TTS error:', err);
      alert('Failed to generate audio. Please try again.');
    }
  };

  const closeAudioPlayer = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setCurrentPlayingSection(null);
  };

  // Receipt modal handlers
  const handleSendReceipt = async () => {
    try {
      const response = await axios.post(`${API}/api/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, {
        withCredentials: true
      });
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      console.error('❌ Receipt email error:', error.response?.data || error.message);
      toast.error(`Receipt email not sent: ${error.response?.data?.detail || error.message}`);
      // Still close the modal even if email sending fails
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header with Logo and Navigation - Only show when NOT displaying results */}
      {!result && (
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/Digital_Ink_AI_Logo.png" 
                  alt="Digital Ink AI Logo" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/homepage')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  🏠 Home
                </button>
                <button
                  onClick={() => navigate('/user_dashboard')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  📊 Dashboard
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title - Only show when NOT displaying results */}
        {!result && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                📊 Competitor & Comparative Analysis Tool
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Gain a strategic edge with expert-level competitive analysis. Upload your manuscript to receive comprehensive market research including competitor identification (3-5 comparable titles with strengths and weaknesses), market gap analysis, differentiation strategies across content/brand/marketing dimensions, and a prioritized action plan. Understand where your book fits in the current market landscape and discover opportunities to stand out from the competition.
              </p>
            </div>

            {/* Pricing Info */}
            {user && (
              <div className="mb-8 bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-200 rounded-lg p-6">
                <div className="text-center">
                  {user.subscription_status !== 'None' ? (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - analyze as many manuscripts as you need!</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - $7.00</h3>
                      <p className="text-blue-700">Professional competitive analysis charged to your payment method on file</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-300 text-green-700 px-6 py-4 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Main Content */}
        {!result && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Analysis Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200">
                {/* Form Header with Logo */}
                <div className="p-6 border-b border-slate-200">
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <h2 className="text-2xl font-bold text-slate-900">📄 Manuscript Submission</h2>
                    <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                  </div>
                </div>

                {/* Form Content */}
                <div className="p-6">
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Book Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  autoComplete="off"
                  placeholder="Enter your book title"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subtitle <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              {/* Author Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Author Name *
                </label>
                <input
                  type="text"
                  name="author_name"
                  value={formData.author_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Publisher */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Publisher <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="publisher"
                  value={formData.publisher}
                  onChange={handleInputChange}
                  placeholder="e.g., Penguin Random House, Self-Published"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              {/* Genre */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Primary Genre *
                </label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Genre</option>
                  <option value="fiction">Fiction</option>
                  <option value="non_fiction">Non-Fiction</option>
                  <option value="mystery">Mystery/Thriller</option>
                  <option value="romance">Romance</option>
                  <option value="science_fiction">Science Fiction</option>
                  <option value="fantasy">Fantasy</option>
                  <option value="horror">Horror</option>
                  <option value="biography">Biography/Memoir</option>
                  <option value="self_help">Self-Help</option>
                  <option value="business">Business</option>
                  <option value="young_adult">Young Adult</option>
                  <option value="children">Children's</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Sub-Genre */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sub-Genre <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="sub_genre"
                  value={formData.sub_genre}
                  onChange={handleInputChange}
                  placeholder="e.g., Psychological Thriller, Historical Romance"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              {/* File Upload - Moved to bottom */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Manuscript File * <span className="text-gray-500 font-normal">(PDF or DOCX, max 10MB)</span>
                </label>
                
                {/* DEBUG: Temporarily removed accept attribute to test */}
                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
                  <input
                    type="file"
                    id="ccat-file-upload"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="ccat-file-upload" className="cursor-pointer block">
                    <div className="text-4xl mb-4">📁</div>
                    {file && !fileError ? (
                      <div>
                        <p className="text-green-600 font-medium">✓ {file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-700 font-medium">🔧 DEBUG MODE: Click to select ANY file</p>
                        <p className="text-sm text-gray-500">Accept filter temporarily removed for testing</p>
                      </div>
                    )}
                  </label>
                </div>
                {fileError && (
                  <p className="mt-2 text-sm text-red-600">{fileError}</p>
                )}
                
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <strong>DEBUG INFO:</strong> Accept filter removed. Try selecting your .pdf or .docx file now. 
                  If the Open button works, we know it's an accept filter issue and can fix it permanently.
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing || !file}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                {isProcessing ? 'Analyzing...' : 'Analyze this Manuscript with Digital Ink AI'}
              </button>
            </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Analysis Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Analysis Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-teal-500 mr-2">✓</span>
                    <span>3-5 Competitor identification with detailed analysis</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-teal-500 mr-2">✓</span>
                    <span>Market gaps & opportunity assessment</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-teal-500 mr-2">✓</span>
                    <span>Content differentiation strategies</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-teal-500 mr-2">✓</span>
                    <span>Brand positioning recommendations</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-teal-500 mr-2">✓</span>
                    <span>Marketing channel optimization</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-teal-500 mr-2">✓</span>
                    <span>Executive summary for stakeholders</span>
                  </div>
                </div>
              </div>

              {/* File Requirements */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">📋 Requirements</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Format:</strong> PDF or DOCX</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Size:</strong> Up to 10MB</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Content:</strong> Minimum 100 words</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Analysis Time:</strong> 2-5 minutes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
    {result && (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
            {/* Header - ABG Style */}
            <header className="bg-white shadow-sm border-b print:hidden">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Print Header - Hidden on Screen */}
                <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-300">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                        alt="Digital Ink AI" 
                        className="h-12 w-auto"
                      />
                      <div>
                        <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                        <p className="text-sm text-gray-600">Competitor & Comparative Analysis Tool</p>
                        <p className="text-xs text-gray-600 mt-1">Book: {result.title} | Author: {result.author_name}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                      <p>{new Date().toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Report Metadata - ABG Style */}
                <div className="border-b pb-6 mb-6 print:border-b-2">
                  <div className="flex justify-between items-start mb-4 print:mb-2">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">CCAT Analysis Report</h2>
                      <p className="text-slate-600 mt-1">Competitive Analysis for {result.title}</p>
                      <p className="text-slate-600 text-sm">by {result.author_name}</p>
                    </div>
                    <div className="flex gap-3 print:hidden">
                      <button onClick={handlePrint} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">🖨️ Print</button>
                      <button onClick={exportToPDF} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">📄 Export PDF</button>
                      <button 
                        onClick={() => {
                          setResult(null);
                          setFormData({
                            title: '',
                            subtitle: '',
                            author_name: '',
                            publisher: '',
                            genre: '',
                            sub_genre: ''
                          });
                          setFile(null);
                          navigate('/ccat');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                      >
                        ✨ New Analysis
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Book Title:</span>
                      <p className="font-semibold text-slate-900">{result.title || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Author:</span>
                      <p className="font-semibold text-slate-900">{result.author_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Service:</span>
                      <p className="font-semibold text-slate-900">CCAT</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Genre:</span>
                      <p className="font-semibold text-slate-900">{result.genre ? result.genre.replace('_', ' ').toUpperCase() : 'N/A'}</p>
                    </div>
                  </div>
                </div>

              <div id="ccat-results" className="space-y-8 print-section">
                
                {/* Charge Info */}
                {result.charged && (
                  <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-emerald-800 mb-2">✅ Analysis Complete</h3>
                    <p className="text-emerald-700">
                      Service charge: ${result.charge_amount?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-sm text-emerald-600 mt-1">
                      You earned {Math.floor((result.charge_amount || 0) * 10)} reward points!
                    </p>
                  </div>
                )}

                {/* Analysis Details Card */}
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200 rounded-lg shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">📋 Analysis Details</h2>
                  
                  {/* Prominent Title and Author */}
                  <div className="bg-white p-4 rounded-lg shadow-sm text-center mb-4">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{result.title || 'Manuscript Analysis'}</h2>
                    {result.subtitle && (
                      <p className="text-lg text-slate-700 italic mb-2">{result.subtitle}</p>
                    )}
                    <p className="text-xl text-slate-800 mb-3">by {result.author_name || 'Unknown Author'}</p>
                    
                    {result.publisher && (
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <span className="text-sm text-slate-600 font-semibold">Publisher: </span>
                        <span className="text-base text-slate-800">{result.publisher}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Book Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                      <div className="text-2xl font-bold text-teal-600">{result.word_count?.toLocaleString() || 'N/A'}</div>
                      <div className="text-sm text-slate-600 mt-1">Word Count</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                      <div className="text-2xl font-bold text-green-600">{result.page_count || 'N/A'}</div>
                      <div className="text-sm text-slate-600 mt-1">Est. Pages</div>
                    </div>
                    {result.genre && (
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <div className="text-lg font-bold text-purple-600">{result.genre.replace('_', ' ').toUpperCase()}</div>
                        <div className="text-sm text-slate-600 mt-1">Genre</div>
                      </div>
                    )}
                    {result.sub_genre && (
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <div className="text-lg font-bold text-indigo-600">{result.sub_genre}</div>
                        <div className="text-sm text-slate-600 mt-1">Sub-Genre</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Print Header - Hidden on Screen */}
                <div className="print-only mb-6 pb-4 border-b-2 border-gray-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                      <p className="text-sm text-gray-600">Competitor & Comparative Analysis Tool</p>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                      <p>{new Date().toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* ONLY Show Individual Formatted Sections with Read Aloud - NO raw analysis_report */}
                {result.book_information_overview && (
                  <div className="bg-white border-slate-200 rounded-lg shadow-md">
                    <div className="bg-gradient-to-r from-slate-50 to-teal-50 p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">📖 Book Information Overview</h2>
                        <div className="no-print">
                          <ReadAloudButton
                            text={result.book_information_overview}
                            onPlay={() => handleReadAloud(result.book_information_overview, 'Book Info')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="prose max-w-none whitespace-pre-wrap text-slate-800" style={{ fontFamily: 'Times New Roman, serif' }}>{cleanText(result.book_information_overview)}</div>
                    </div>
                  </div>
                )}

                {result.competitor_identification && (
                  <div className="bg-white border-slate-200 rounded-lg shadow-md">
                    <div className="bg-gradient-to-r from-slate-50 to-teal-50 p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">📚 Competitor Identification</h2>
                        <div className="no-print">
                          <ReadAloudButton
                            text={result.competitor_identification}
                            onPlay={() => handleReadAloud(result.competitor_identification, 'Competitors')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="prose max-w-none whitespace-pre-wrap text-slate-800" style={{ fontFamily: 'Times New Roman, serif' }}>{cleanText(result.competitor_identification)}</div>
                    </div>
                  </div>
                )}

                {result.market_gaps_analysis && (
                  <div className="bg-white border-slate-200 rounded-lg shadow-md">
                    <div className="bg-gradient-to-r from-slate-50 to-teal-50 p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">🔍 Market Gaps & Opportunities</h2>
                        <div className="no-print">
                          <ReadAloudButton
                            text={result.market_gaps_analysis}
                            onPlay={() => handleReadAloud(result.market_gaps_analysis, 'Market Gaps')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="prose max-w-none whitespace-pre-wrap text-slate-800" style={{ fontFamily: 'Times New Roman, serif' }}>{cleanText(result.market_gaps_analysis)}</div>
                    </div>
                  </div>
                )}

                {result.differentiation_strategies && (
                  <div className="bg-white border-slate-200 rounded-lg shadow-md">
                    <div className="bg-gradient-to-r from-slate-50 to-teal-50 p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">💡 Differentiation Strategies</h2>
                        <div className="no-print">
                          <ReadAloudButton
                            text={result.differentiation_strategies}
                            onPlay={() => handleReadAloud(result.differentiation_strategies, 'Differentiation')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="prose max-w-none whitespace-pre-wrap text-slate-800" style={{ fontFamily: 'Times New Roman, serif' }}>{cleanText(result.differentiation_strategies)}</div>
                    </div>
                  </div>
                )}

                {result.recommendations && (
                  <div className="bg-white border-slate-200 rounded-lg shadow-md">
                    <div className="bg-gradient-to-r from-slate-50 to-teal-50 p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">📋 Recommendations & Action Plan</h2>
                        <div className="no-print">
                          <ReadAloudButton
                            text={result.recommendations}
                            onPlay={() => handleReadAloud(result.recommendations, 'Recommendations')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="prose max-w-none whitespace-pre-wrap text-slate-800" style={{ fontFamily: 'Times New Roman, serif' }}>{cleanText(result.recommendations)}</div>
                    </div>
                  </div>
                )}

                {result.final_summary && (
                  <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200 rounded-lg shadow-md">
                    <div className="bg-gradient-to-r from-teal-100 to-blue-100 p-4 border-b border-teal-300">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900">📄 Executive Summary</h2>
                        <div className="no-print">
                          <ReadAloudButton
                            text={result.final_summary}
                            onPlay={() => handleReadAloud(result.final_summary, 'Summary')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="prose max-w-none whitespace-pre-wrap text-slate-800" style={{ fontFamily: 'Times New Roman, serif' }}>{cleanText(result.final_summary)}</div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </main>
          </div>
        )}
      </main>

      {/* Modals */}
      {isProcessing && (
        <ProcessingModal
          isOpen={isProcessing}
          serviceName="Competitive & Category Analysis Tool (CCAT)"
          bookTitle={formData.title}
          progress={processingProgress}
          currentStep={currentStep}
          progressSteps={progressSteps}
        />
      )}

      {/* Charge Confirmation Modal */}
      <ServiceChargeModal
        isOpen={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        onConfirm={handleConfirmCharge}
        serviceInfo={chargeInfo}
        userType={user?.role || 'a_la_carte'}
      />

      {showPaymentModal && paymentInfo && (
        <ServiceChargeModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={paymentInfo.amount}
          serviceName="CCAT"
          message={paymentInfo.message}
          upgradeFlow={paymentInfo.upgrade_flow}
        />
      )}

      {audioUrl && (
        <AudioPlayer
          audioUrl={audioUrl}
          onClose={closeAudioPlayer}
          sectionName={currentPlayingSection}
        />
      )}

      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendReceipt}
        serviceName="CCAT"
        amount={lastCharge?.amount || 0}
        bookTitle={lastCharge?.bookTitle || formData.title || ''}
      />

      {/* Print Styles - ONLY applies during printing */}
      <style>{`
        @media print {
          /* Hide everything except report content */
          body * {
            visibility: hidden;
          }
          
          /* Make report content visible */
          #ccat-results,
          #ccat-results * {
            visibility: visible;
          }
          
          /* Position report at top */
          #ccat-results {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          /* Hide no-print elements */
          .no-print {
            display: none !important;
          }
          
          /* Show print-only elements */
          .print-only {
            display: block !important;
          }
          
          /* Typography for print */
          * {
            font-family: 'Times New Roman', serif !important;
          }
          
          h1, h2, h3 {
            color: #000 !important;
          }
          
          /* Content */
          .prose {
            font-size: 12pt;
            line-height: 1.5;
            color: #000 !important;
          }
          
          /* Section spacing */
          .space-y-8 > * {
            margin-bottom: 1rem !important;
            page-break-inside: avoid;
          }
          
          /* Remove gradients for print */
          .bg-gradient-to-r {
            background: white !important;
          }
          
          /* Keep borders */
          .border-slate-200,
          .border-teal-200 {
            border: 1px solid #ccc !important;
          }
        }
        
        .print-only {
          display: none;
        }
      `}</style>
    </div>
  );
}

export default CCATPage;
